import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';

interface CategoryHeaderTextColorSelectorProps {
  value: string;
  primaryColor: string;
  secondaryColor: string;
  onChange: (color: string) => void;
}

/**
 * CategoryHeaderTextColorSelector - Color picker for category header text
 */
const CategoryHeaderTextColorSelector: React.FC<CategoryHeaderTextColorSelectorProps> = ({
  value,
  primaryColor,
  secondaryColor,
  onChange
}) => {
  const presetColors = [
    { name: 'Primary Colour', value: primaryColor },
    { name: 'Secondary Colour', value: secondaryColor },
    { name: 'White', value: '#ffffff' },
    { name: 'Black', value: '#000000' }
  ];

  const handlePresetClick = (color: string) => {
    onChange(color);
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const getColorName = (color: string) => {
    const preset = presetColors.find(p => p.value.toLowerCase() === color.toLowerCase());
    return preset ? preset.name : 'Custom';
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Category Header Text Colour</Label>
        <div className="text-xs text-gray-500">
          {getColorName(value)}
        </div>
      </div>
      
      <div className="space-y-3">
        {/* Preset Colors */}
        <div className="grid grid-cols-2 gap-2">
          {presetColors.map((preset) => (
            <Button
              key={preset.name}
              variant={value.toLowerCase() === preset.value.toLowerCase() ? "default" : "outline"}
              size="sm"
              onClick={() => handlePresetClick(preset.value)}
              className="justify-start gap-2"
            >
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: preset.value }}
              />
              {preset.name}
            </Button>
          ))}
        </div>
        
        {/* Custom Color Picker */}
        <div className="space-y-2">
          <Label className="text-xs text-gray-600">Custom Colour</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
              >
                <div 
                  className="w-4 h-4 rounded border border-gray-300"
                  style={{ backgroundColor: value }}
                />
                {value}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64">
              <div className="space-y-3">
                <Label className="text-sm font-medium">Pick a custom colour</Label>
                <Input
                  type="color"
                  value={value}
                  onChange={handleCustomColorChange}
                  className="w-full h-10"
                />
                <Input
                  type="text"
                  value={value}
                  onChange={handleCustomColorChange}
                  placeholder="#ffffff"
                  className="font-mono text-sm"
                />
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      
      <div className="text-xs text-gray-500">
        Choose from preset colours or pick a custom colour for category headers
      </div>
    </div>
  );
};

export default CategoryHeaderTextColorSelector;